// Lib
import React, { useCallback, useEffect, useRef, useState } from 'react';

// Components
import CreateOrganizationModal from '@components/CreateOrganizationModal';
import Menu from '@components/AccountMenu/Menu';

// Hooks
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// Actions
import { ClosePopup, OpenPopup } from '@actions/popup';
import useOrganizationInvitations, {
  type PendingOrganizationInvitation,
} from '@hooks/useOrganizationInvitations';

// Types
import type { Organization } from '@app-types/index';

interface Profile {
  _id?: string;
  username?: string;
  email?: string;
  onboardingPassed?: boolean;
  avatar?: {
    url?: string;
  };
}

interface AccountState {
  profile?: Profile;
  organizations?: Organization[];
}

interface ReduxState {
  Account?: AccountState;
  SelectedOrganization?: Organization;
}

interface UseAccountMenuReturn {
  onCreateOrganization: () => void;
  isShowMenu: boolean;
  setIsShowMenu: React.Dispatch<React.SetStateAction<boolean>>;
  menuRef: React.RefObject<HTMLDivElement | null>;
  buttonRef: React.RefObject<HTMLDivElement | null>;
  isOnboardingPassed: boolean;
  profile: Profile | undefined;
  organizations: Organization[] | undefined;
  selectedOrganization: Organization | undefined;
  onOpenMenuModal: () => void;
  invitations: PendingOrganizationInvitation[];
  busyInvitationId: string | null;
  acceptInvitation: (invitationId: string) => Promise<Organization | null>;
  declineInvitation: (invitationId: string) => Promise<boolean>;
}

const useAccountMenu = (): UseAccountMenuReturn => {
  const [isShowMenu, setIsShowMenu] = useState<boolean>(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { isOnboardingPassed, profile, organizations, selectedOrganization } =
    useSelector((state: ReduxState) => ({
      isOnboardingPassed:
        !!state?.Account?.profile?.onboardingPassed &&
        !!state?.Account?.organizations &&
        state?.Account?.organizations?.length > 0,
      profile: state?.Account?.profile,
      organizations: state?.Account?.organizations,
      selectedOrganization: state?.SelectedOrganization,
    }));
  const { invitations, busyInvitationId, acceptInvitation, declineInvitation } =
    useOrganizationInvitations(isOnboardingPassed);

  useEffect(() => {
    const handleClick = (event: MouseEvent): void => {
      if (menuRef?.current) {
        if (menuRef?.current?.contains(event.target as Node)) return;

        if (buttonRef?.current) {
          if (!buttonRef?.current?.contains(event.target as Node))
            setIsShowMenu(false);
        }
      }
    };

    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, []);

  // A brand organization is its property: "New property" is the full-screen
  // onboarding in its add-a-property mode (new organization + property +
  // channels). Creator organizations have no property: "New organization"
  // keeps the organization form.
  const isCreatorOrg = selectedOrganization?.type === 'creator';
  const onCreateOrganization = useCallback((): void => {
    if (!isCreatorOrg) {
      dispatch(ClosePopup());
      navigate('/onboarding/start?mode=new-property');
      return;
    }
    dispatch(
      OpenPopup({
        children: <CreateOrganizationModal />,
        onDone: null,
        onCancel: null,
        outerAnimation: null,
        innerAnimation: null,
        isCancelAnimation: true,
        positiveText: null,
        negativeText: null,
        // The modal renders its own themed header + close button, so suppress
        // the generic (white, absolutely-placed) Popup chrome.
        closable: false,
        isTheme: true,
        style: {
          padding: '0px',
          maxWidth: '680px',
          width: '100%',
          background: 'var(--popover)',
          borderRadius: '16px',
          border: '1px solid var(--border)',
          boxShadow: '4px 4px 16px rgba(0, 0, 0, 0.07)',
          ['@media(max-width: 743px)']: {
            borderRadius: '20px 20px 0px 0px',
            margin: 'auto auto 0px',
            width: '100%',
          },
        },
      }),
    );
  }, [dispatch, navigate, isCreatorOrg]);

  const onOpenMenuModal = useCallback((): void => {
    dispatch(
      OpenPopup({
        children: (
          <div className="flex flex-col gap-3">
            <Menu
              organizations={organizations}
              selectedOrganization={selectedOrganization}
              profile={profile}
              onCreateOrganization={onCreateOrganization}
              setIsShowMenu={setIsShowMenu}
              closeModal={() => dispatch(ClosePopup())}
              invitations={invitations}
              busyInvitationId={busyInvitationId}
              onAcceptInvitation={acceptInvitation}
              onDeclineInvitation={declineInvitation}
            />
          </div>
        ),
        onDone: null,
        onCancel: null,
        outerAnimation: null,
        innerAnimation: null,
        isCancelAnimation: true,
        positiveText: null,
        negativeText: null,
        closable: true,
        isTheme: true,
        style: {
          padding: '32px',
          maxWidth: '800px',
          width: '100%',
          background: 'var(--popover)',
          borderRadius: '16px',
          border: '1px solid var(--border)',
          boxShadow: '4px 4px 16px rgba(0, 0, 0, 0.07)',
          '&&>div:first-child': {
            position: 'absolute !important',
          },
          ['@media(max-width: 743px)']: {
            padding: '20px',
            borderRadius: '20px 20px 0px 0px',
            margin: 'auto auto 0px',
            width: '100%',
          },
        },
      }),
    );
  }, [
    dispatch,
    onCreateOrganization,
    selectedOrganization,
    organizations,
    profile,
    invitations,
    busyInvitationId,
    acceptInvitation,
    declineInvitation,
  ]);

  return {
    onCreateOrganization,
    isShowMenu,
    setIsShowMenu,
    menuRef,
    buttonRef,
    isOnboardingPassed,
    profile,
    organizations,
    selectedOrganization,
    onOpenMenuModal,
    invitations,
    busyInvitationId,
    acceptInvitation,
    declineInvitation,
  };
};

export default useAccountMenu;
